/**
 * DossierItemCard
 * Affiche un item du dossier avec ses champs éditables et actions
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ExternalLink, AlertTriangle, Send, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { ITEM_STATUS } from '../constants';

export const DossierItemCard = ({
  item,
  idx,
  site,
  dossier,
  itemDrafts,
  savingItem,
  publishingItem,
  onSetDraft,
  onSaveItem,
  onPublishNow,
  onOpenScheduleModal,
}) => {
  const navigate = useNavigate();
  const draft = itemDrafts[item.id] || {};
  const get = (k, fallback) => draft[k] !== undefined ? draft[k] : (item[k] ?? fallback);
  const dirty = Object.keys(draft).length > 0;
  
  const statusInfo = ITEM_STATUS[item.status] || ITEM_STATUS.planned;
  const StatusIcon = statusInfo.icon;
  
  const isLocked = ['generating', 'generated', 'published', 'scheduled', 'publishing'].includes(item.status);
  const isPublishable = item.status === 'generated' && !item.wordpress_post_id;
  const isScheduled = item.status === 'scheduled';
  
  const suggestedDate = dossier.j0_date
    ? new Date(new Date(dossier.j0_date).getTime() + (item.planned_publish_offset_days || 0) * 86400000)
    : null;

  return (
    <Card 
      className={`border ${item.role === 'pilier' ? 'border-amber-500/50 bg-amber-950/10' : 'border-cyan-500/30 bg-slate-900/40'}`} 
      data-testid={`item-card-${item.id}`}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={item.role === 'pilier' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'}>
              {item.role === 'pilier' ? '★ PILIER' : `#${idx}`}
            </Badge>
            <span className="text-slate-300">{site?.name || item.site_id}</span>
            <Badge className={`text-[10px] border ${statusInfo.color}`} data-testid={`item-status-${item.id}`}>
              <StatusIcon className={`w-3 h-3 mr-1 inline ${statusInfo.spin ? 'animate-spin' : ''}`} />
              {statusInfo.label}
            </Badge>
            {item.article_id && (
              <Button
                onClick={() => navigate(`/article/${item.article_id}`)}
                variant="outline"
                size="sm"
                className="h-6 text-[10px] px-2"
                data-testid={`view-article-${item.id}`}
              >
                <ExternalLink className="w-3 h-3 mr-1" /> Voir l&apos;article
              </Button>
            )}
          </div>
          <Button
            onClick={() => onSaveItem(item)}
            disabled={!dirty || savingItem === item.id || isLocked}
            size="sm"
            variant={dirty ? 'default' : 'outline'}
            className={dirty ? 'bg-emerald-600 hover:bg-emerald-500' : ''}
            data-testid={`save-item-${item.id}`}
            title={isLocked ? 'Édition désactivée après génération' : ''}
          >
            <Save className="w-3 h-3 mr-1" /> {savingItem === item.id ? '…' : 'Enregistrer'}
          </Button>
        </CardTitle>
        
        {(item.predicted_url || item.slug) && (
          <div className="text-[10px] text-slate-500 truncate mt-1">
            Slug : <span className="text-cyan-400 font-mono">{item.slug || '—'}</span>
            {item.predicted_url && (
              <> · URL prévue : <span className="text-slate-400 font-mono">{item.predicted_url}</span></>
            )}
          </div>
        )}
        
        {item.wordpress_link && (
          <div className="text-[10px] mt-1 flex items-center gap-1">
            <ExternalLink className="w-3 h-3 text-green-400" />
            <a href={item.wordpress_link} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 font-mono truncate" data-testid={`wp-link-${item.id}`}>
              {item.wordpress_link}
            </a>
            {isScheduled && item.scheduled_for && (
              <span className="text-blue-300 ml-2">— prévu {new Date(item.scheduled_for).toLocaleString('fr-FR')}</span>
            )}
          </div>
        )}
        
        {suggestedDate && !item.wordpress_link && (
          <div className="text-[10px] text-slate-500 mt-1">
            Date suggérée : <span className="text-amber-400">{suggestedDate.toLocaleDateString('fr-FR')} (J+{item.planned_publish_offset_days || 0})</span>
          </div>
        )}
        
        {item.error && (
          <div className="text-[11px] text-red-400 mt-1 flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> <span>{item.error}</span>
          </div>
        )}
      </CardHeader>
      
      <CardContent className="space-y-2 pt-0">
        {/* Boutons de publication */}
        {isPublishable && (
          <div className="flex gap-2 pb-2 border-b border-slate-700/50 mb-2">
            <Button
              onClick={() => onPublishNow(item, site)}
              disabled={publishingItem === item.id}
              size="sm"
              className="bg-green-600 hover:bg-green-500"
              data-testid={`publish-now-${item.id}`}
            >
              <Send className="w-3 h-3 mr-1" /> {publishingItem === item.id ? 'Publication…' : 'Publier maintenant'}
            </Button>
            <Button
              onClick={() => onOpenScheduleModal(item)}
              disabled={publishingItem === item.id}
              size="sm"
              variant="outline"
              className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10"
              data-testid={`schedule-${item.id}`}
            >
              <Clock className="w-3 h-3 mr-1" /> Planifier…
            </Button>
          </div>
        )}
        
        {/* Champs éditables */}
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Angle</label>
          <Input 
            value={get('angle', '')} 
            onChange={e => onSetDraft(item.id, 'angle', e.target.value)} 
            disabled={isLocked} 
            data-testid={`angle-input-${item.id}`} 
          />
        </div>
        
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Titre</label>
          <Input 
            value={get('title', '')} 
            onChange={e => onSetDraft(item.id, 'title', e.target.value)} 
            disabled={isLocked} 
            data-testid={`title-input-${item.id}`} 
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Mot-clé principal</label>
            <Input 
              value={get('main_keyword', '')} 
              onChange={e => onSetDraft(item.id, 'main_keyword', e.target.value)} 
              disabled={isLocked} 
              data-testid={`mkw-input-${item.id}`} 
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] text-slate-500 uppercase">Mots-clés secondaires (virgule)</label>
            <Input
              value={draft.secondary_keywords !== undefined ? draft.secondary_keywords : (item.secondary_keywords || []).join(', ')}
              onChange={e => onSetDraft(item.id, 'secondary_keywords', e.target.value)}
              disabled={isLocked}
              data-testid={`skw-input-${item.id}`}
            />
          </div>
        </div>
        
        <div>
          <label className="text-[10px] text-slate-500 uppercase">Prompt de rédaction</label>
          <Textarea 
            value={get('article_prompt', '')} 
            onChange={e => onSetDraft(item.id, 'article_prompt', e.target.value)} 
            disabled={isLocked} 
            rows={4} 
            data-testid={`prompt-input-${item.id}`} 
          />
        </div>
        
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-slate-500 uppercase">Publication J+</label>
          <Input
            type="number"
            min="0"
            max="60"
            value={get('planned_publish_offset_days', 0)}
            onChange={e => onSetDraft(item.id, 'planned_publish_offset_days', e.target.value)}
            disabled={isLocked}
            className="w-24"
            data-testid={`offset-input-${item.id}`}
          />
          <span className="text-xs text-slate-500">jour(s) après J0</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default DossierItemCard;
